const SESSION_TOKEN_KEY = "ths-basecamp-guest-session";

let fallbackSessionToken: string | null = null;
let selectedAssignmentId: string | null = null;
let migrated = false;
let pendingWrite: { previous: string | null; token: string | null } | null =
  null;
const localListeners = new Set<() => void>();

export function getStoredToken(): string | null {
  try {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (!migrated) {
      migrated = true;
      let legacyToken: string | null = null;
      try {
        legacyToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
      } catch {
        // A blocked legacy store must not hide the durable credential.
      }
      if (!token && legacyToken) {
        storeToken(legacyToken);
        return legacyToken;
      }
    }
    if (pendingWrite && token === pendingWrite.previous) {
      return pendingWrite.token;
    }
    pendingWrite = null;
    fallbackSessionToken = token;
    return token;
  } catch {
    return fallbackSessionToken;
  }
}

function writeToken(token: string | null): void {
  if (getStoredToken() !== token) selectedAssignmentId = null;
  fallbackSessionToken = token;
  migrated = true;
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
    pendingWrite = null;
  } catch {
    // Retain this page's identity when storage is unavailable or read-only.
    pendingWrite = { previous, token };
  }
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // Kiosk sessions never use this store.
  }
  localListeners.forEach((listener) => listener());
}

export function storeToken(token: string): void {
  writeToken(token);
}

export function clearStoredToken(): void {
  writeToken(null);
  selectedAssignmentId = null;
}

/** Observe other tabs and reconcile when a suspended page returns. */
export function subscribeStoredToken(onChange: () => void): () => void {
  let observed = getStoredToken();
  const acceptLocalWrite = () => {
    observed = getStoredToken();
  };
  const reconcile = () => {
    const token = getStoredToken();
    if (token === observed) return;
    observed = token;
    selectedAssignmentId = null;
    onChange();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key === SESSION_TOKEN_KEY || event.key === null) reconcile();
  };
  localListeners.add(acceptLocalWrite);
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", reconcile);
  window.addEventListener("pageshow", reconcile);
  document.addEventListener("visibilitychange", reconcile);
  return () => {
    localListeners.delete(acceptLocalWrite);
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", reconcile);
    window.removeEventListener("pageshow", reconcile);
    document.removeEventListener("visibilitychange", reconcile);
  };
}

export function getSelectedAssignmentId(): string | null {
  return selectedAssignmentId;
}

export function selectAssignment(assignmentId: string): void {
  selectedAssignmentId = assignmentId;
}

export function clearSelectedAssignment(): void {
  selectedAssignmentId = null;
}
